import React, { useEffect, useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import { DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getDoc, Timestamp, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { formatInTimeZone } from 'date-fns-tz';

// Fixed timezone for consistent display across all users (US Central Time)
const DISPLAY_TIMEZONE = 'America/Chicago';

// 引入与UserRegistrations组件相同的接口定义
interface Registration {
    id: string;
    program?: EntityReference;
    programTitle?: string;
    courses?: EntityReference[];
    courseNames?: string[];
    courseAvatar?: string;
    user?: EntityReference;
    
    firstName?: string;
    lastName?: string;
    chineseName?: string;
    gender?: string;
    dateOfBirth?: Timestamp;
    grade?: string;
    allergies?: string;
    
    parentFirstName?: string;
    parentLastName?: string;
    phone?: string;
    email?: string;
    address?: string;
    aptNumber?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    
    emergencyFirstName?: string;
    emergencyLastName?: string;
    emergencyPhone?: string;
    emergencyEmail?: string;
    emergencyAddress?: string;
    emergencyAptNumber?: string;
    emergencyCity?: string;
    emergencyState?: string;
    emergencyZipCode?: string;
    parentMessage?: string;
    
    status: string;
    createdAt: Timestamp;
    updatedAt?: Timestamp;
    
    totalPrice: number;
    earlyBirdPrice: number;
    amountPaid: number;
    receipt?: string;
    
    selectedDates?: Timestamp[];
}

interface EntityReference {
    id: string;
    path: string;
}

interface RegistrationDetailProps {
    registration: Registration;
    isOpen: boolean;
    onClose: () => void;
}

const RegistrationDetail: React.FC<RegistrationDetailProps> = ({ 
    registration, 
    isOpen, 
    onClose 
}) => {
    const { t, i18n } = useTranslation();
    const [loadingCourses, setLoadingCourses] = useState(false);
    const [courseData, setCourseData] = useState<{
        names: string[];
        avatar: string | null;
    }>({
        names: [],
        avatar: null
    });
    
    // 在组件打开时加载课程信息
    useEffect(() => {
        const fetchCoursesData = async () => {
            if (!isOpen || !registration.courses || registration.courses.length === 0) return;
            
            try {
                setLoadingCourses(true);
                
                // 获取当前语言
                const currentLanguage = t('common.languageCode') === 'zh' ? 'zh' : 'en';
                
                // 批量获取课程信息
                const coursePromises = registration.courses.map(
                    async (courseRef: EntityReference) => {
                        try {
                            // 确保路径格式正确 (应该是 collection/id)
                            const segments = courseRef.path.split('/');
                            const collectionId = segments[0]; // 如 "courses"
                            const docId = courseRef.id;
                            
                            // 使用正确的文档引用方式
                            const courseDoc = await getDoc(doc(db, collectionId, docId));
                            
                            if (courseDoc.exists()) {
                                const courseData = courseDoc.data();
                                
                                // 根据当前语言选择标题
                                let courseTitle = t('registration.unknownCourse');
                                if (currentLanguage === 'zh' && courseData?.name) {
                                    courseTitle = courseData.name; // 中文标题
                                } else if (currentLanguage === 'en' && courseData?.nameEn) {
                                    courseTitle = courseData.nameEn; // 英文标题
                                } else if (courseData?.name) {
                                    courseTitle = courseData.name; // 回退到中文标题
                                }
                                
                                return {
                                    name: courseTitle,
                                    imageUrl: courseData?.avatar || null // 使用正确的图片字段名(avatar)
                                };
                            }
                            return {
                                name: t('registration.unknownCourse'),
                                imageUrl: null
                            };
                        } catch (err) {
                            console.error('获取课程信息失败:', err, courseRef);
                            return {
                                name: t('registration.unknownCourse'),
                                imageUrl: null
                            };
                        }
                    }
                );
                
                const courses = await Promise.all(coursePromises);
                
                // 提取课程名称和第一个课程的图片（如果有）
                const courseNames = courses.map(course => course.name);
                const firstCourseWithImage = courses.find(course => course.imageUrl);
                
                setCourseData({
                    names: courseNames,
                    avatar: firstCourseWithImage?.imageUrl || null
                });
                
            } catch (err) {
                console.error('获取课程数据失败:', err);
            } finally {
                setLoadingCourses(false);
            }
        };
        
        fetchCoursesData();
    }, [isOpen, registration.courses, t]);
    
    const formatDate = (timestamp: Timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate();
        // Use consistent timezone for display to avoid timezone conversion issues
        return formatInTimeZone(date, DISPLAY_TIMEZONE, 'M/d/yyyy');
    };
    
    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'waitlisted':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'cancelled':
                return 'bg-gray-100 text-gray-800 border-gray-200';
            case 'pending':
            default:
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        }
    };
    
    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-indigo-900/70 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900 to-indigo-900 px-4 pb-4 pt-5 text-left shadow-2xl transition-all border border-white/20 sm:my-8 sm:w-full sm:max-w-3xl sm:p-6">
                                <div className="absolute top-0 right-0 pt-4 pr-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-transparent text-blue-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">{t('common.close')}</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                
                                <div>
                                    <motion.div 
                                        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/30 border border-blue-300/50 shadow-lg"
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <DocumentTextIcon className="h-8 w-8 text-blue-100" aria-hidden="true" />
                                    </motion.div>
                                    
                                    <motion.div 
                                        className="mt-4 text-center"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-white">
                                            {t('registrationSuccess.registrationDetails')}
                                        </Dialog.Title>
                                    </motion.div>
                                </div>
                                
                                <div className="mt-6 space-y-5">
                                    <motion.div
                                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.1 }}
                                    >
                                        <div className="flex items-center mb-4">
                                            {courseData.avatar && (
                                                <div className="w-16 h-16 rounded-md overflow-hidden mr-4 flex-shrink-0">
                                                    <img 
                                                        src={courseData.avatar} 
                                                        alt={registration.programTitle || ""}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                            )}
                                            <div>
                                                <h3 className="text-xl font-semibold text-white">{registration.programTitle}</h3>
                                                <p className="text-blue-200">{t('profile.registrationId')}: {registration.id}</p>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                                            <div>
                                                <p className="text-sm opacity-80">{t('profile.registrationDate')}:</p>
                                                <p>{registration.createdAt ? formatDate(registration.createdAt) : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('profile.amountPaid')}:</p>
                                                <p>${registration.amountPaid}</p>
                                            </div>
                                            {/* <div>
                                                <p className="text-sm opacity-80">{t('profile.earlyBirdPrice')}:</p>
                                                <p>${registration.earlyBirdPrice}</p>
                                            </div> */}
                                            <div>
                                                <p className="text-sm opacity-80">{t('profile.registrationStatus.label')}:</p>
                                                <span className={`px-2 py-1 text-xs rounded-full border inline-block ${getStatusBadgeClass(registration.status)}`}>
                                                    {t(`profile.registrationStatus.${registration.status}`)}
                                                </span>
                                            </div>
                                            {registration.receipt && (
                                                <div className="col-span-2 mt-2">
                                                    <p className="text-sm opacity-80 mb-1">{t('profile.receipt')}:</p>
                                                    <a 
                                                        href={registration.receipt} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                        </svg>
                                                        {t('profile.viewReceipt')}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {loadingCourses ? (
                                            <div className="mt-4 flex justify-center">
                                                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-200 border-t-transparent"></div>
                                            </div>
                                        ) : courseData.names.length > 0 ? (
                                            <div className="mt-4">
                                                <p className="text-sm opacity-80 mb-2 text-white">{t('registrationSuccess.courses')}:</p>
                                                <div className="space-y-1">
                                                    {courseData.names.map((name, i) => (
                                                        <div key={i} className="text-blue-100 bg-blue-900/30 px-3 py-2 rounded">
                                                            • {name}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </motion.div>
                                    
                                    <motion.div
                                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.2 }}
                                    >
                                        <h3 className="text-lg font-semibold text-white mb-4">{t('registration.studentInfo')}</h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.firstName')}:</p>
                                                <p>{registration.firstName || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.lastName')}:</p>
                                                <p>{registration.lastName || '-'}</p>
                                            </div>
                                            {registration.chineseName && (
                                                <div>
                                                    <p className="text-sm opacity-80">{t('registration.chineseName')}:</p>
                                                    <p>{registration.chineseName}</p>
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.gender')}:</p>
                                                <p>{registration.gender ? t(`registration.${registration.gender}`) : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.dateOfBirth')}:</p>
                                                <p>{registration.dateOfBirth ? formatDate(registration.dateOfBirth) : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.grade')}:</p>
                                                <p>{registration.grade ? t(`registration.grades.${registration.grade}`) : '-'}</p>
                                            </div>
                                            {registration.allergies && (
                                                <div className="col-span-2">
                                                    <p className="text-sm opacity-80">{t('registration.allergies')}:</p>
                                                    <p>{registration.allergies}</p>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                    
                                    <motion.div
                                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.3 }}
                                    >
                                        <h3 className="text-lg font-semibold text-white mb-4">{t('registration.parentInfo')}</h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.parentFirstName')}:</p>
                                                <p>{registration.parentFirstName || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.parentLastName')}:</p>
                                                <p>{registration.parentLastName || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.phone')}:</p>
                                                <p>{registration.phone || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.email')}:</p>
                                                <p>{registration.email || '-'}</p>
                                            </div>
                                            <div className="col-span-2">
                                                <p className="text-sm opacity-80">{t('registration.address')}:</p>
                                                <p>
                                                    {registration.address || '-'}
                                                    {registration.aptNumber && `, ${registration.aptNumber}`}
                                                    {registration.city && `, ${registration.city}`}
                                                    {registration.state && `, ${registration.state}`}
                                                    {registration.zipCode && ` ${registration.zipCode}`}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                    
                                    <motion.div
                                        className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: 0.4 }}
                                    >
                                        <h3 className="text-lg font-semibold text-white mb-4">{t('registration.emergencyContact')}</h3>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-100">
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.emergencyFirstName')}:</p>
                                                <p>{registration.emergencyFirstName || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.emergencyLastName')}:</p>
                                                <p>{registration.emergencyLastName || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.emergencyPhone')}:</p>
                                                <p>{registration.emergencyPhone || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm opacity-80">{t('registration.emergencyEmail')}:</p>
                                                <p>{registration.emergencyEmail || '-'}</p>
                                            </div>
                                            <div className="col-span-2">
                                                <p className="text-sm opacity-80">{t('registration.emergencyAddress')}:</p>
                                                <p>
                                                    {registration.emergencyAddress || '-'}
                                                    {registration.emergencyAptNumber && `, ${registration.emergencyAptNumber}`}
                                                    {registration.emergencyCity && `, ${registration.emergencyCity}`}
                                                    {registration.emergencyState && `, ${registration.emergencyState}`}
                                                    {registration.emergencyZipCode && ` ${registration.emergencyZipCode}`}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                    
                                    {registration.parentMessage && (
                                        <motion.div
                                            className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.5 }}
                                        >
                                            <h3 className="text-lg font-semibold text-white mb-4">{t('registration.parentMessage')}</h3>
                                            <p className="text-blue-100 whitespace-pre-line">{registration.parentMessage}</p>
                                        </motion.div>
                                    )}
                                    
                                    {registration.selectedDates && registration.selectedDates.length > 0 && (
                                        <motion.div
                                            className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/10"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: 0.5 }}
                                        >
                                            <h3 className="text-lg font-semibold text-white mb-4">{t('profile.selectedDates')}</h3>
                                            
                                            <div className="flex flex-wrap gap-2">
                                                {registration.selectedDates.map((date, i) => (
                                                    <span key={i} className="px-3 py-1 text-sm bg-blue-900/70 text-blue-100 rounded-full border border-blue-700">
                                                        {formatDate(date)}
                                                    </span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </div>
                                
                                <div className="mt-8 flex justify-end">
                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-full bg-blue-500 bg-opacity-80 px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800 transition-all duration-200"
                                        onClick={onClose}
                                    >
                                        {t('common.close')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default RegistrationDetail; 